/**
 * QuickCalendar — Calendario rápido en la barra superior.
 * Muestra:
 *  - Fecha de hoy marcada
 *  - Días festivos oficiales de México (verde)
 *  - Días festivos federales de Estados Unidos (azul)
 *  - Días que caen en ambos (gradiente)
 *  - Leyenda + nombre del feriado al pasar el mouse
 */
import { useState, useMemo } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarDays } from "lucide-react";

// ─────────── Utilidades de cálculo de feriados ───────────
const nthWeekdayOfMonth = (year, month, weekday, n) => {
  // month 0-11; weekday 0=Sun..6=Sat; n 1..5
  const firstDay = new Date(year, month, 1).getDay();
  const offset = (weekday - firstDay + 7) % 7;
  return new Date(year, month, 1 + offset + (n - 1) * 7);
};
const lastWeekdayOfMonth = (year, month, weekday) => {
  const lastDay = new Date(year, month + 1, 0);
  const offset = (lastDay.getDay() - weekday + 7) % 7;
  return new Date(year, month, lastDay.getDate() - offset);
};
// Cálculo de Pascua (algoritmo de Meeus/Jones/Butcher)
const easterSunday = (year) => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const L = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * L) / 451);
  const month = Math.floor((h + L - 7 * m + 114) / 31);
  const day = ((h + L - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
};
const shiftDays = (date, n) => {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
};
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// Feriados oficiales de México (Ley Federal del Trabajo Art. 74 + días cívicos comunes)
const holidaysMX = (year) => {
  const easter = easterSunday(year);
  return [
    { date: new Date(year, 0, 1), name: "Año Nuevo" },
    { date: nthWeekdayOfMonth(year, 1, 1, 1), name: "Día de la Constitución" }, // 1er lunes febrero
    { date: nthWeekdayOfMonth(year, 2, 1, 3), name: "Natalicio de Benito Juárez" }, // 3er lunes marzo
    { date: shiftDays(easter, -3), name: "Jueves Santo" },
    { date: shiftDays(easter, -2), name: "Viernes Santo" },
    { date: new Date(year, 4, 1), name: "Día del Trabajo" },
    { date: new Date(year, 4, 5), name: "Batalla de Puebla (5 de mayo)" },
    { date: new Date(year, 8, 16), name: "Día de la Independencia" },
    { date: new Date(year, 10, 2), name: "Día de Muertos" },
    { date: nthWeekdayOfMonth(year, 10, 1, 3), name: "Día de la Revolución" }, // 3er lunes noviembre
    { date: new Date(year, 11, 12), name: "Virgen de Guadalupe" },
    { date: new Date(year, 11, 25), name: "Navidad" },
  ];
};

// Feriados federales de Estados Unidos
const holidaysUS = (year) => [
  { date: new Date(year, 0, 1), name: "New Year's Day" },
  { date: nthWeekdayOfMonth(year, 0, 1, 3), name: "Martin Luther King Jr. Day" },
  { date: nthWeekdayOfMonth(year, 1, 1, 3), name: "Presidents' Day" },
  { date: lastWeekdayOfMonth(year, 4, 1), name: "Memorial Day" },
  { date: new Date(year, 5, 19), name: "Juneteenth" },
  { date: new Date(year, 6, 4), name: "Independence Day" },
  { date: nthWeekdayOfMonth(year, 8, 1, 1), name: "Labor Day" },
  { date: nthWeekdayOfMonth(year, 9, 1, 2), name: "Columbus Day" },
  { date: new Date(year, 10, 11), name: "Veterans Day" },
  { date: nthWeekdayOfMonth(year, 10, 4, 4), name: "Thanksgiving" },
  { date: new Date(year, 11, 25), name: "Christmas Day" },
];

export default function QuickCalendar() {
  const [open, setOpen] = useState(false);
  const today = useMemo(() => new Date(), []);
  const [selected, setSelected] = useState(today);
  const [month, setMonth] = useState(today);

  // Feriados del año visualizado y adyacentes (para cross-year)
  const { mxMap, usMap, mxDates, usDates, bothDates } = useMemo(() => {
    const y = month.getFullYear();
    const _mxMap = new Map();
    const _usMap = new Map();
    for (const yr of [y - 1, y, y + 1]) {
      for (const h of holidaysMX(yr)) _mxMap.set(iso(h.date), h.name);
      for (const h of holidaysUS(yr)) _usMap.set(iso(h.date), h.name);
    }
    const mx = [];
    const us = [];
    const both = [];
    const seen = new Set([..._mxMap.keys(), ..._usMap.keys()]);
    seen.forEach((k) => {
      const [yr, m, d] = k.split("-").map(Number);
      const date = new Date(yr, m - 1, d);
      const inMX = _mxMap.has(k);
      const inUS = _usMap.has(k);
      if (inMX && inUS) both.push(date);
      else if (inMX) mx.push(date);
      else if (inUS) us.push(date);
    });
    return { mxMap: _mxMap, usMap: _usMap, mxDates: mx, usDates: us, bothDates: both };
  }, [month]);

  // Nombre del feriado del día seleccionado (para el tooltip inferior)
  const selectedHolidayInfo = useMemo(() => {
    if (!selected) return null;
    const key = iso(selected);
    const mx = mxMap.get(key);
    const us = usMap.get(key);
    if (!mx && !us) return null;
    return { mx, us };
  }, [selected, mxMap, usMap]);

  const modifiers = { mx: mxDates, us: usDates, both: bothDates };
  const modifiersClassNames = {
    mx: "bg-emerald-100 text-emerald-800 font-semibold rounded-md hover:bg-emerald-200",
    us: "bg-blue-100 text-blue-800 font-semibold rounded-md hover:bg-blue-200",
    both: "bg-gradient-to-br from-emerald-200 to-blue-200 text-slate-800 font-semibold rounded-md",
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="hidden lg:inline-flex items-center justify-center text-cyan-100 bg-white/10 rounded-lg w-7 h-7 border border-white/10 hover:bg-white/20 hover:text-white transition-colors"
          title="Calendario"
          data-testid="quick-calendar-trigger"
        >
          <CalendarDays className="w-4 h-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={10}
        className="w-auto p-0 rounded-2xl border border-slate-200 shadow-2xl overflow-hidden"
        data-testid="quick-calendar-popover"
      >
        {/* Header brand */}
        <div className="bg-gradient-to-r from-[#00157F] to-[#0037FF] px-4 py-2.5 flex items-center gap-2 text-white">
          <CalendarDays className="w-4 h-4" />
          <button
            onClick={() => { setSelected(today); setMonth(today); }}
            className="leading-tight text-left flex-1 group rounded-md -mx-1 px-1 py-0.5 hover:bg-white/10 transition-colors cursor-pointer"
            title="Ir a la fecha de hoy"
            data-testid="quick-calendar-today"
          >
            <p className="text-[13px] font-semibold flex items-center gap-1.5">
              Calendario
              <span className="text-[9px] font-normal text-cyan-200 opacity-0 group-hover:opacity-100 transition-opacity">
                · click para ir a hoy
              </span>
            </p>
            <p className="text-[10px] text-cyan-200 underline decoration-cyan-300/40 decoration-dotted underline-offset-2">
              Hoy: {today.toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </p>
          </button>
        </div>

        {/* Calendar */}
        <div className="bg-white">
          <Calendar
            mode="single"
            selected={selected}
            onSelect={(d) => d && setSelected(d)}
            month={month}
            onMonthChange={setMonth}
            modifiers={modifiers}
            modifiersClassNames={modifiersClassNames}
            weekStartsOn={1}
            data-testid="quick-calendar-picker"
          />
        </div>

        {/* Info feriado seleccionado */}
        {selectedHolidayInfo && (
          <div className="border-t border-slate-100 px-3 py-2 space-y-1" data-testid="quick-calendar-selected-info">
            {selectedHolidayInfo.mx && (
              <div className="flex items-start gap-1.5 text-xs">
                <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 mt-1 shrink-0" />
                <span><b className="text-emerald-800">🇲🇽 MX:</b> <span className="text-slate-700">{selectedHolidayInfo.mx}</span></span>
              </div>
            )}
            {selectedHolidayInfo.us && (
              <div className="flex items-start gap-1.5 text-xs">
                <span className="inline-block w-2 h-2 rounded-full bg-blue-500 mt-1 shrink-0" />
                <span><b className="text-blue-800">🇺🇸 US:</b> <span className="text-slate-700">{selectedHolidayInfo.us}</span></span>
              </div>
            )}
          </div>
        )}

        {/* Leyenda */}
        <div className="border-t border-slate-100 bg-slate-50 px-3 py-2 flex items-center justify-between text-[11px]">
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-3.5 h-3.5 rounded bg-emerald-200 border border-emerald-300" />
            <span className="text-slate-600">🇲🇽 México</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-3.5 h-3.5 rounded bg-blue-200 border border-blue-300" />
            <span className="text-slate-600">🇺🇸 USA</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-3.5 h-3.5 rounded bg-gradient-to-br from-emerald-200 to-blue-200 border border-slate-300" />
            <span className="text-slate-600">ambos</span>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
